import React from "react";
import { motionSafe as motion } from "@/lib/motionSafe";
import { Linkedin } from "lucide-react";
import { useI18n } from "../../i18n";

const PORTRAIT = "https://customer-assets-lqy194kg.emergentagent.net/job_compliance-hub-863/artifacts/1u2k6i6i_ProfilePicture.jpg";
const LINKEDIN_URL = "https://www.linkedin.com/in/nextechlaw/";

const fadeUp = {
  hidden: { opacity: 1, y: 14 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

export const Founder = () => {
  const { t } = useI18n();
  return (
    <section id="founder" data-testid="founder-section" className="relative py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }}
          className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#3B82F6] mb-6"
        >
          {t.founder.overline}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Portrait */}
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="lg:col-span-5 relative"
          >
            <div className="relative overflow-hidden rounded-sm bg-[#030712]">
              <img
                src={PORTRAIT}
                alt="Michael — Founder NexTech.Law"
                className="w-full h-[420px] md:h-[520px] object-cover transition-all duration-700 [filter:grayscale(0.4)_contrast(1.05)_brightness(0.95)] [object-position:center_22%]"
              />
              {/* Subtle blue duotone tint — anchors the portrait in the brand
                  palette without losing skin-tone warmth. */}
              <div className="absolute inset-0 bg-[#0052FF] mix-blend-color opacity-[0.22] pointer-events-none" />
              {/* Subtle warm vignette + soft bottom fade for text legibility below */}
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0b1220]/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#030712]/80 to-transparent pointer-events-none" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 pointer-events-none" />
            </div>
            {/* Name plate + LinkedIn button below the portrait — fills the
                left column so it balances the longer text on the right. */}
            <div className="mt-6 flex items-start justify-between gap-4 pb-4 border-b border-white/10">
              <div className="min-w-0">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[#3B82F6]">
                  FOUNDER · CRA SPECIALIST
                </div>
                <div className="mt-2 font-display text-2xl md:text-3xl font-extrabold text-white leading-[1.1] tracking-tight text-balance">
                  {t.founder.fullName}
                </div>
              </div>
            </div>
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-linkedin"
              className="mt-5 group inline-flex items-center justify-between gap-3 w-full bg-white/[0.03] hover:bg-[#0052FF]/15 border border-white/10 hover:border-[#0052FF] text-white text-sm font-medium px-5 py-4 rounded-sm transition-colors duration-200"
            >
              <span className="inline-flex items-center gap-3">
                <Linkedin size={18} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                {t.founder.linkedin}
              </span>
              <span className="font-mono-tech text-[10px] tracking-[0.2em] uppercase text-gray-400 group-hover:text-white transition-colors">
                @nextechlaw
              </span>
            </a>
          </motion.div>

          {/* Text + stats */}
          <div className="lg:col-span-7">
            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="font-display font-extrabold tracking-tight text-4xl md:text-5xl leading-[1.02] text-white"
            >
              {t.founder.title}
            </motion.h2>
            <motion.p
              variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="mt-6 text-base md:text-lg text-gray-400 leading-relaxed max-w-2xl"
            >
              {t.founder.body}
            </motion.p>
            {t.founder.highlight && (
              <motion.p
                variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={{ once: true }}
                className="mt-6 max-w-2xl pl-5 border-l-2 border-[#0052FF] font-display text-xl md:text-2xl font-bold text-white leading-snug"
              >
                {t.founder.highlight}
              </motion.p>
            )}

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2">
              {t.founder.stats.map((s, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
                  className="glass p-6 border-white/10 -ml-px -mt-px"
                  data-testid={`founder-stat-${i}`}
                >
                  <div className="font-display text-2xl md:text-3xl font-extrabold text-white leading-[1.05] tracking-tight text-balance">{s.v}</div>
                  <div className="mt-3 text-sm text-gray-400 leading-snug">{s.l}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
